import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlass';
import { SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { SerializedSmartLinkVersionTheme } from 'fym-shared/src/smartlinks/types';
import { TypedMemo } from '../../../common/types';
import { PickerGroup, PickerItem, searchPickerItems } from './pickerItems';
import { pickerMessages as m } from './pickerMessages';
import { SMLPickerCard } from './SMLPickerCard';
import { SMLPickerChips, PickerChip } from './SMLPickerChips';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './SMLBlockPicker.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SMLPickerBlocksProps {
  items: readonly PickerItem[];
  isBioLink: boolean;
  theme: SerializedSmartLinkVersionTheme;
  onPick: (item: PickerItem) => void;
}

const GROUP_TITLES = { music: m.groupMusic, grow: m.groupGrow, links: m.groupLinks, about: m.groupAbout } as const;
const GROUPS: readonly PickerGroup[] = ['music', 'grow', 'links', 'about'];

/** The cards of the picker (design b1–b4): search, chips, the recommended group, then every block by what it does. */
const SMLPickerBlocksComponent: React.FC<SMLPickerBlocksProps> = ({ items, isBioLink, theme, onPick }) => {
  const intl = useIntl();
  const [query, setQuery] = useState('');
  const [chip, setChip] = useState<PickerChip>('all');
  const onQuery = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value), []);
  const clear = useCallback(() => setQuery(''), []);
  const showCatalogLinks = useCallback(() => setQuery('pre-save'), []);
  const results = useMemo(() => searchPickerItems(items, query), [items, query]);
  const isSearching = query.trim() !== '';
  // A block the Bio Link has already goes to the end of its group, faded (design b4).
  const sorted = useMemo(
    () => [...items].sort((a, b) => Number(a.onPageUUID !== null) - Number(b.onPageUUID !== null)),
    [items]
  );
  const recommended = useMemo(() => sorted.filter((item) => item.recommended), [sorted]);
  const groups = useMemo(
    () =>
      GROUPS.filter((group) => chip === 'all' || chipMatches(chip, group)).map((group) => ({
        group,
        items: sorted.filter(
          (item) =>
            item.group === group &&
            (chip !== 'pictures' || item.type !== SmartLinkWidgetType.link) &&
            (chip !== 'links' || item.type === SmartLinkWidgetType.link)
        ),
      })),
    [chip, sorted]
  );
  const renderCards = (list: readonly PickerItem[]) => (
    <div className={styles.grid}>
      {list.map((item) => (
        <SMLPickerCard key={item.type} item={item} theme={theme} query={query} onPick={onPick} />
      ))}
    </div>
  );

  return (
    <div className={styles.body}>
      <div className={clsx(styles.search, isSearching && styles.searchOn)}>
        <FontAwesomeIcon icon={faMagnifyingGlass} />
        <input
          value={query}
          onChange={onQuery}
          placeholder={intl.formatMessage(m.search)}
          aria-label={intl.formatMessage(m.search)}
          data-testid="picker-search"
        />
      </div>
      {isSearching ? (
        <>
          <span className={styles.groupTitle}>{intl.formatMessage(m.results, { count: results.length })}</span>
          {results.length > 0 ? (
            renderCards(results)
          ) : (
            <div className={styles.empty} data-testid="picker-empty">
              <b>{intl.formatMessage(m.noMatch, { query })}</b>
              <span>{intl.formatMessage(m.noMatchHint)}</span>
              <span className={styles.emptyActions}>
                <button type="button" className={styles.emptyButton} onClick={showCatalogLinks}>
                  {intl.formatMessage(m.showCatalogLinks)}
                </button>
                <button type="button" className={styles.emptyLink} onClick={clear}>
                  {intl.formatMessage(m.clearSearch)}
                </button>
              </span>
            </div>
          )}
        </>
      ) : (
        <>
          <SMLPickerChips value={chip} onChange={setChip} />
          {chip === 'all' && (
            <div className={styles.group} data-testid="picker-recommended">
              <span className={styles.groupTitle}>
                {intl.formatMessage(isBioLink ? m.recommendedBio : m.recommendedSong)}
                <span className={styles.groupWhy}>
                  {intl.formatMessage(isBioLink ? m.recommendedBioWhy : m.recommendedSongWhy)}
                </span>
              </span>
              {renderCards(recommended)}
            </div>
          )}
          {groups.map(({ group, items: groupItems }) =>
            groupItems.length === 0 ? null : (
              <div key={group} className={styles.group}>
                <span className={styles.groupTitle}>{intl.formatMessage(GROUP_TITLES[group])}</span>
                {renderCards(groupItems)}
              </div>
            )
          )}
        </>
      )}
    </div>
  );
};

function chipMatches(chip: PickerChip, group: PickerGroup): boolean {
  if (chip === 'links' || chip === 'pictures') {
    return group === 'links';
  }
  return chip === group;
}

export const SMLPickerBlocks = TypedMemo(SMLPickerBlocksComponent);
